/**
 * Narrowcasting — Create narrowcasting video presentations
 * Flow: klant → template → presentatie aanmaken/bewerken
 * Stitch design system
 */
import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { Box, Typography, Autocomplete, TextField, Dialog, Tooltip } from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import toast from 'react-hot-toast';
import NarrowcastingEditor from './NarrowcastingEditor';
import PageHeader from '../../ui-components/PageHeader';

/* ── Stitch tokens ─────────────────────────────────────────────────────────── */
const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  violet: '#7c3aed', violetBg: '#f5f3ff', violetBorder: '#ddd6fe', violetDark: '#5b21b6',
  bg: '#f8f9fa', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7',
  blue: '#3b82f6', blueBg: '#dbeafe',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const IconBadge = ({ name, size = 48, iconSize = 26, bg = T.primaryContainer, color = T.primary }) => (
  <Box sx={{ width: size, height: size, borderRadius: '0.75rem', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    <Icon name={name} size={iconSize} sx={{ color }} />
  </Box>
);

const Badge = ({ label, color = T.primary, bg = T.primaryContainer }) => (
  <Typography component="span" sx={{
    fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700,
    color, bgcolor: bg, px: 1.25, py: 0.3, borderRadius: '9999px',
    display: 'inline-block', textTransform: 'uppercase', letterSpacing: '0.06em',
  }}>
    {label}
  </Typography>
);

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
    {children}
  </Typography>
);

/* ── Helpers ────────────────────────────────────────────────────────────────── */
function getBeeldmerkUrl(option) {
  const bm = option?.fields?.Beeldmerk;
  if (Array.isArray(bm) && bm.length > 0) return bm[0]?.thumbnails?.small?.url || bm[0]?.url || null;
  return null;
}

const STATUS_MAP = {
  draft: { bg: T.surfaceLow, color: T.outline, label: 'Concept', icon: 'edit_note' },
  rendering: { bg: T.amberBg, color: '#d97706', label: 'Renderen', icon: 'hourglass_top' },
  concatenating: { bg: T.blueBg, color: T.blue, label: 'Samenvoegen', icon: 'merge' },
  completed: { bg: T.emeraldBg, color: T.emerald, label: 'Voltooid', icon: 'check_circle' },
  error: { bg: T.errorBg, color: T.error, label: 'Fout', icon: 'error' },
};

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function Narrowcasting() {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const userEmail = activeAccount?.username || '';

  const [klanten, setKlanten] = useState([]);
  const [loadingKlanten, setLoadingKlanten] = useState(false);
  const [selectedKlant, setSelectedKlant] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingHuisstijl, setLoadingHuisstijl] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [presentations, setPresentations] = useState([]);
  const [loadingPresentations, setLoadingPresentations] = useState(false);
  const [editingPresentation, setEditingPresentation] = useState(null);
  const [newDialog, setNewDialog] = useState(false);
  const [newNaam, setNewNaam] = useState('');
  const [newOutputFormat, setNewOutputFormat] = useState('mp4');

  useEffect(() => { fetchKlanten(); }, []);
  useEffect(() => {
    if (selectedKlant) { fetchHuisstijl(selectedKlant.id); fetchPresentations(selectedKlant.id); }
    else { setTemplates([]); setPresentations([]); setSelectedTemplate(null); }
  }, [selectedKlant]);

  /* ── Data fetching ─────────────────────────────────────────────────────── */
  const fetchKlanten = async () => {
    setLoadingKlanten(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/klanten`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Klanten ophalen mislukt');
      setKlanten(data.klanten || []);
    } catch (err) { console.error('Error fetching klanten:', err); toast.error('Klanten ophalen mislukt'); }
    finally { setLoadingKlanten(false); }
  };

  const fetchHuisstijl = async (klantId) => {
    setLoadingHuisstijl(true);
    setTemplates([]);
    setSelectedTemplate(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/${klantId}/huisstijl`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Huisstijl ophalen mislukt');
      setTemplates((data.huisstijl || []).filter(r => r.fields?.Type === 'Narrowcasting'));
    } catch (err) { console.error('Error fetching huisstijl:', err); toast.error('Huisstijl ophalen mislukt'); }
    finally { setLoadingHuisstijl(false); }
  };

  const fetchPresentations = async (klantId) => {
    setLoadingPresentations(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/narrowcasting/presentations?accountId=${klantId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Presentaties ophalen mislukt');
      setPresentations(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Error fetching presentations:', err); setPresentations([]); }
    finally { setLoadingPresentations(false); }
  };

  /* ── Create / Delete ───────────────────────────────────────────────────── */
  const handleCreatePresentation = async () => {
    if (!newNaam.trim() || !selectedTemplate) return;
    try {
      const cfgs = selectedTemplate.configuraties || [];
      const slideCfgs = cfgs.filter(c => { const t = c.fields?.Type || ''; return t !== 'Background' && t !== 'Muziek'; });
      const bgCfgs = cfgs.filter(c => c.fields?.Type === 'Background');
      const muziekCfgs = cfgs.filter(c => c.fields?.Type === 'Muziek');

      let slides;
      if (slideCfgs.length > 0) {
        const sorted = [...slideCfgs].sort((a, b) => {
          const typeA = a.fields?.Type || '';
          const typeB = b.fields?.Type || '';
          const order = (t) => t.includes('Begin') ? 0 : t.includes('Eind') ? 2 : 1;
          if (order(typeA) !== order(typeB)) return order(typeA) - order(typeB);
          return typeA.localeCompare(typeB);
        });
        slides = sorted.map((c, i) => {
          const type = c.fields?.Type || '';
          const numId = typeof c.id === 'string' && c.id.startsWith('pg_') ? parseInt(c.id.slice(3), 10) : c.id;
          return { slide_type: type.includes('Begin') ? 'begin' : type.includes('Eind') ? 'eind' : 'content', sort_order: i, config_id: numId, layers: c.fields?.Velden || [], duration: 6 };
        });
      } else {
        slides = [
          { slide_type: 'begin', sort_order: 0, duration: 6 },
          { slide_type: 'content', sort_order: 1, duration: 6 },
          { slide_type: 'eind', sort_order: 2, duration: 6 },
        ];
      }

      const defaultBgId = bgCfgs.length > 0 ? bgCfgs[0].id : null;
      const defaultMuziekId = muziekCfgs.length > 0 ? muziekCfgs[0].id : null;

      const res = await authFetch(`${BACKEND_URL}/Api/narrowcasting/presentations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ naam: newNaam.trim(), accountId: selectedKlant.id, huisstijlElementId: selectedTemplate.id, format: '16x9', outputFormat: newOutputFormat, createdBy: userEmail, slides }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Aanmaken mislukt');
      setNewDialog(false);
      setNewNaam('');
      toast.success('Presentatie aangemaakt');
      setEditingPresentation({ ...data, _defaultBgId: defaultBgId, _defaultMuziekId: defaultMuziekId });
    } catch (err) { toast.error(err.message); }
  };

  const handleDeletePresentation = async (id) => {
    if (!(await bevestig({ titel: 'Presentatie verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/narrowcasting/presentations/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      toast.success('Presentatie verwijderd');
      fetchPresentations(selectedKlant.id);
    } catch (err) { toast.error(err.message); }
  };

  /* ── Editor view ───────────────────────────────────────────────────────── */
  if (editingPresentation) {
    return (
      <NarrowcastingEditor
        presentationId={editingPresentation.id}
        defaultBgId={editingPresentation._defaultBgId}
        defaultMuziekId={editingPresentation._defaultMuziekId}
        onBack={() => { setEditingPresentation(null); if (selectedKlant) fetchPresentations(selectedKlant.id); }}
      />
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <PageHeader
        icon="tv"
        title="Narrowcasting"
        subtitle="Multi-slide video presentaties"
        accent={{ color: '#635983', bg: '#dbcdff' }}
      />

      {/* ── Klant selectie ──────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, mb: 3, boxShadow: T.shadow }}>
        <SectionLabel>Selecteer klant</SectionLabel>
        <Autocomplete
          options={klanten}
          getOptionLabel={(option) => option.fields?.Account || ''}
          value={selectedKlant}
          onChange={(_, value) => setSelectedKlant(value)}
          loading={loadingKlanten}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Zoek op klantnaam..."
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.5rem', fontSize: '0.84rem', bgcolor: T.surfaceLow,
                  fontFamily: T.fontBody,
                  '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
                  '&.Mui-focused fieldset': { borderColor: T.primary },
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              {getBeeldmerkUrl(option) ? (
                <Box component="img" src={getBeeldmerkUrl(option)} sx={{ width: 28, height: 28, borderRadius: '0.35rem', objectFit: 'contain', bgcolor: T.surfaceLow, p: 0.25, flexShrink: 0 }} />
              ) : (
                <Box sx={{ width: 28, height: 28, borderRadius: '0.35rem', bgcolor: T.surfaceLow, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: T.outline }}>{(option.fields?.Account || '?').substring(0, 2).toUpperCase()}</Typography>
                </Box>
              )}
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', color: T.text }}>{option.fields?.Account}</Typography>
            </Box>
          )}
          noOptionsText="Geen klanten gevonden"
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </Box>

      {/* ── Template selectie ───────────────────────────────────────────── */}
      {selectedKlant && (
        <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, mb: 3, boxShadow: T.shadow }}>
          <SectionLabel>Kies template</SectionLabel>

          {loadingHuisstijl ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              {[1, 2, 3].map(i => (
                <Box key={i} sx={{
                  height: 160, borderRadius: '0.75rem', bgcolor: T.surfaceLow,
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
                }} />
              ))}
            </Box>
          ) : templates.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, borderRadius: '0.75rem', bgcolor: T.surfaceLow }}>
              <Icon name="info" size={18} sx={{ color: T.outline, marginTop: 1 }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, lineHeight: 1.6 }}>
                Geen Narrowcasting templates gevonden. Voeg er een toe via de huisstijl pagina van deze klant.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              {templates.map((t) => {
                const naam = t.fields?.Naam || 'Template';
                const preview = t.fields?.Preview;
                const previewUrl = Array.isArray(preview) && preview.length > 0 ? (preview[0].thumbnails?.large?.url || preview[0].url) : null;
                const cfgs = t.configuraties || [];
                const selected = selectedTemplate?.id === t.id;

                return (
                  <Box
                    key={t.id}
                    onClick={() => setSelectedTemplate(t)}
                    sx={{
                      borderRadius: '0.75rem', overflow: 'hidden', cursor: 'pointer',
                      border: `2px solid ${selected ? T.violet : 'transparent'}`,
                      bgcolor: T.surface, boxShadow: T.shadow,
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: selected ? T.violet : `${T.violet}40`,
                        boxShadow: '0 8px 24px rgba(45,51,53,0.08)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {previewUrl ? (
                      <Box component="img" src={previewUrl} alt={naam} sx={{
                        width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block',
                        borderBottom: `1px solid ${T.outlineVariant}20`,
                      }} />
                    ) : (
                      <Box sx={{
                        width: '100%', aspectRatio: '16/9', bgcolor: T.violetBg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon name="tv" size={36} sx={{ color: T.violetBorder }} />
                      </Box>
                    )}
                    <Box sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <Icon name="tv" size={16} sx={{ color: T.violet }} />
                        <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 700, color: T.text }}>
                          {naam}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {cfgs.length > 0 && <Badge label={`${cfgs.length} slides`} color={T.violetDark} bg={T.violetBg} />}
                        {t.fields?.Merk && <Badge label={t.fields.Merk} color={T.outline} bg={T.surfaceLow} />}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ── Presentaties ────────────────────────────────────────────────── */}
      {selectedKlant && selectedTemplate && (
        <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, boxShadow: T.shadow }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <SectionLabel>Presentaties</SectionLabel>
            <Box sx={{ flex: 1 }} />

            {/* Refresh */}
            <Tooltip title="Vernieuwen" arrow
              slotProps={{
                tooltip: { sx: { fontFamily: T.fontBody, fontSize: '0.72rem', bgcolor: T.text, borderRadius: '0.5rem', px: 1.5, py: 0.75 } },
                arrow: { sx: { color: T.text, '&::before': { border: 'none' } } },
              }}
            >
              <Box
                onClick={() => fetchPresentations(selectedKlant.id)}
                sx={{ width: 32, height: 32, borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', '&:hover': { bgcolor: T.surfaceLow } }}
              >
                <Icon name="refresh" size={18} sx={{ color: T.outline }} />
              </Box>
            </Tooltip>

            {/* New presentation button */}
            <Box
              onClick={() => setNewDialog(true)}
              sx={{
                px: 2, py: 0.75, borderRadius: '0.5rem', cursor: 'pointer',
                background: `linear-gradient(135deg, ${T.violet} 0%, #6d28d9 100%)`,
                color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.78rem',
                boxShadow: '0 4px 12px rgba(124,58,237,0.25)',
                display: 'flex', alignItems: 'center', gap: 0.5,
                transition: 'all 0.2s',
                '&:hover': { boxShadow: '0 6px 20px rgba(124,58,237,0.35)', transform: 'translateY(-1px)' },
              }}
            >
              <Icon name="add" size={16} sx={{ color: '#fff' }} />
              Nieuwe presentatie
            </Box>
          </Box>

          {loadingPresentations ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {[1, 2].map(i => (
                <Box key={i} sx={{
                  height: 64, borderRadius: '0.5rem', bgcolor: T.surfaceLow,
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
                }} />
              ))}
            </Box>
          ) : presentations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Icon name="tv_off" size={40} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 12px' }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', color: T.outline }}>
                Nog geen presentaties
              </Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outlineVariant, mt: 0.25 }}>
                Maak er een aan om te beginnen
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {presentations.map((p) => {
                const st = STATUS_MAP[p.status] || STATUS_MAP.draft;
                return (
                  <Box
                    key={p.id}
                    onClick={() => setEditingPresentation(p)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2, p: 1.75,
                      borderRadius: '0.5rem', cursor: 'pointer',
                      bgcolor: T.surfaceLow,
                      border: '1px solid transparent',
                      transition: 'all 0.15s',
                      '&:hover': { bgcolor: T.violetBg, borderColor: `${T.violet}20` },
                    }}
                  >
                    <IconBadge name="tv" size={38} iconSize={20} bg={T.violetBg} color={T.violet} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', fontWeight: 700, color: T.text }}>
                        {p.naam}
                      </Typography>
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outlineVariant }}>
                        {new Date(p.updated_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {' · '}{p.output_format?.toUpperCase()}
                      </Typography>
                    </Box>

                    {/* Status badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.35, borderRadius: '9999px', bgcolor: st.bg }}>
                      <Icon name={st.icon} size={12} sx={{ color: st.color }} />
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, color: st.color }}>
                        {st.label}
                      </Typography>
                    </Box>

                    {/* Delete */}
                    <Box
                      onClick={(e) => { e.stopPropagation(); handleDeletePresentation(p.id); }}
                      sx={{
                        width: 30, height: 30, borderRadius: '0.35rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', transition: 'all 0.15s',
                        '&:hover': { bgcolor: T.errorBg },
                      }}
                    >
                      <Icon name="delete" size={16} sx={{ color: T.outlineVariant }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ── New dialog ──────────────────────────────────────────────────── */}
      <Dialog
        open={newDialog}
        onClose={() => setNewDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '0.75rem', p: 0.5 } }}
      >
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.15rem', fontWeight: 800, color: T.text, mb: 2.5 }}>
            Nieuwe presentatie
          </Typography>

          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
            Naam
          </Typography>
          <TextField
            fullWidth size="small" placeholder="Bijv. Lobby scherm Q2"
            value={newNaam} onChange={(e) => setNewNaam(e.target.value)} autoFocus
            sx={{
              mb: 2.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.5rem', fontSize: '0.84rem', bgcolor: T.surfaceLow,
                fontFamily: T.fontBody,
                '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
                '&.Mui-focused fieldset': { borderColor: T.primary },
              },
            }}
          />

          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
            Output formaat
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            {[
              { value: 'mp4', label: 'H.264 (.mp4)', icon: 'videocam' },
              { value: 'mov', label: 'ProRes (.mov)', icon: 'high_quality' },
            ].map(opt => (
              <Box
                key={opt.value}
                onClick={() => setNewOutputFormat(opt.value)}
                sx={{
                  flex: 1, py: 1.25, px: 1.5, borderRadius: '0.5rem', cursor: 'pointer',
                  bgcolor: newOutputFormat === opt.value ? T.violetBg : T.surfaceLow,
                  border: `1.5px solid ${newOutputFormat === opt.value ? `${T.violet}40` : 'transparent'}`,
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: newOutputFormat === opt.value ? T.violetBg : T.surfaceHigh },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Icon name={opt.icon} size={16} sx={{ color: newOutputFormat === opt.value ? T.violet : T.outlineVariant }} />
                  <Typography sx={{
                    fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: newOutputFormat === opt.value ? 700 : 500,
                    color: newOutputFormat === opt.value ? T.violet : T.text,
                  }}>
                    {opt.label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Box
              onClick={() => setNewDialog(false)}
              sx={{
                px: 2, py: 0.75, borderRadius: '0.5rem', cursor: 'pointer',
                fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem', color: T.secondary,
                transition: 'all 0.15s',
                '&:hover': { bgcolor: T.surfaceLow },
              }}
            >
              Annuleren
            </Box>
            <Box
              onClick={newNaam.trim() ? handleCreatePresentation : undefined}
              sx={{
                px: 2.5, py: 0.75, borderRadius: '0.5rem',
                cursor: newNaam.trim() ? 'pointer' : 'default',
                background: newNaam.trim() ? `linear-gradient(135deg, ${T.violet} 0%, #6d28d9 100%)` : T.surfaceHigh,
                color: newNaam.trim() ? '#fff' : T.outlineVariant,
                fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.82rem',
                boxShadow: newNaam.trim() ? '0 4px 12px rgba(124,58,237,0.25)' : 'none',
                transition: 'all 0.2s',
                '&:hover': newNaam.trim() ? { boxShadow: '0 6px 20px rgba(124,58,237,0.35)' } : {},
              }}
            >
              Aanmaken
            </Box>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
